import type { BoardData, Item, RfBand } from '../model/schema.js';
import { bandOf } from './bands.js';

/** A wireless device with a known frequency (OIL-19). */
export interface RfDevice {
  id: string;
  label: string;
  kind: Item['kind'];
  model: string;
  band: RfBand;
  /** MHz. */
  frequency: number;
}

/** Minimum distance between two carriers (MHz) – typical for analogue UHF systems. */
export const MIN_SPACING_MHZ = 0.35;
/** Distance (MHz) at which a third-order intermodulation product hits another carrier. */
export const IMD_SPACING_MHZ = 0.15;
/** Same for fifth-order products (3·f1 − 2·f2), which are weaker (U45). */
export const IMD5_SPACING_MHZ = 0.1;
/** Frequency grid of the suggestion (MHz), the tuning step of most systems. */
export const RF_GRID_MHZ = 0.025;

/** Frequency range (MHz) of a European UHF TV channel (21–69, 8 MHz each, U45). */
export function tvChannelRange(channel: number): [number, number] {
  const lo = 470 + (channel - 21) * 8;
  return [lo, lo + 8];
}

/** The TV channel a frequency lies in (21–69), undefined outside. */
export function tvChannelOf(frequency: number): number | undefined {
  const ch = Math.floor((frequency - 470) / 8) + 21;
  return ch >= 21 && ch <= 69 ? ch : undefined;
}

/** Wireless mics / IEMs with a frequency; a transmitter and its pack count once (same frequency). */
export function rfDevices(data: Pick<BoardData, 'items'>): RfDevice[] {
  const out: RfDevice[] = [];
  for (const i of Object.values(data.items)) {
    const rf = i.rf;
    if (rf?.link !== 'wireless' || rf.frequency === undefined) continue;
    out.push({
      id: i.id,
      label: i.label,
      kind: i.kind,
      model: [i.brand, i.model].filter(Boolean).join(' '),
      band: rf.band ?? bandOf(rf.frequency),
      frequency: rf.frequency,
    });
  }
  return out.sort((a, b) => a.frequency - b.frequency || a.id.localeCompare(b.id));
}

export interface RfConflict {
  kind: 'spacing' | 'imd' | 'imd5' | 'tv';
  /** spacing: the two devices; imd / imd5: the two sources and the hit device; tv: the device. */
  ids: string[];
  /** The frequency of the problem (MHz): the lower carrier / the intermodulation product. */
  frequency: number;
}

const round = (v: number) => Math.round(v * 1000) / 1000;

/**
 * Carriers closer than the minimum spacing, and third-order intermodulation products
 * (2·f1 − f2) of every pair that land on another carrier. Devices on the same frequency that are
 * linked (transmitter + pack) are expected to share it – pass `linked` to skip them.
 */
export function rfConflicts(
  devices: readonly RfDevice[],
  linked: (a: string, b: string) => boolean = () => false,
  opts: { tvChannels?: readonly number[] } = {},
): RfConflict[] {
  const out: RfConflict[] = [];
  const carriers = devices.filter(
    (d, i) => !devices.slice(0, i).some((e) => linked(e.id, d.id) && e.frequency === d.frequency),
  );
  for (let i = 0; i < carriers.length; i++) {
    for (let j = i + 1; j < carriers.length; j++) {
      const a = carriers[i]!;
      const b = carriers[j]!;
      if (Math.abs(a.frequency - b.frequency) < MIN_SPACING_MHZ)
        out.push({
          kind: 'spacing',
          ids: [a.id, b.id],
          frequency: round(Math.min(a.frequency, b.frequency)),
        });
    }
  }
  for (const a of carriers) {
    for (const b of carriers) {
      if (a === b) continue;
      const product = 2 * a.frequency - b.frequency;
      for (const c of carriers) {
        if (c === a || c === b) continue;
        if (Math.abs(c.frequency - product) < IMD_SPACING_MHZ)
          out.push({ kind: 'imd', ids: [a.id, b.id, c.id], frequency: round(product) });
        const fifth = 3 * a.frequency - 2 * b.frequency;
        if (Math.abs(c.frequency - fifth) < IMD5_SPACING_MHZ)
          out.push({ kind: 'imd5', ids: [a.id, b.id, c.id], frequency: round(fifth) });
      }
    }
  }
  // carriers inside a TV channel that is on air at the venue (U45)
  const tv = new Set(opts.tvChannels ?? []);
  for (const c of carriers) {
    const ch = tvChannelOf(c.frequency);
    if (ch !== undefined && tv.has(ch))
      out.push({ kind: 'tv', ids: [c.id], frequency: c.frequency });
  }
  return out;
}

/**
 * The lowest frequency on the 25 kHz grid in `band` (or `range`) that keeps the minimum spacing
 * to every carrier, creates no third- or fifth-order product on a carrier, is hit by none, and
 * avoids the occupied TV channels (U45). Undefined when the band is full.
 */
export function suggestFrequency(
  devices: readonly RfDevice[],
  range: readonly [number, number],
  opts: { tvChannels?: readonly number[] } = {},
): number | undefined {
  const carriers = devices.map((d) => d.frequency);
  const tv = new Set(opts.tvChannels ?? []);
  const near = (x: number, y: number, d: number) => Math.abs(x - y) < d;
  const steps = Math.floor((range[1] - range[0]) / RF_GRID_MHZ + 1e-9);
  for (let k = 0; k <= steps; k++) {
    const f = round(range[0] + k * RF_GRID_MHZ);
    const ch = tvChannelOf(f);
    if (ch !== undefined && tv.has(ch)) continue;
    if (carriers.some((c) => near(c, f, MIN_SPACING_MHZ))) continue;
    const all = [...carriers, f];
    let ok = true;
    outer: for (const a of all) {
      for (const b of all) {
        if (a === b) continue;
        const p3 = 2 * a - b;
        const p5 = 3 * a - 2 * b;
        for (const c of all) {
          if (c === a || c === b) continue;
          // only products that involve the new carrier (as a source or as the one hit)
          if (a !== f && b !== f && c !== f) continue;
          if (near(c, p3, IMD_SPACING_MHZ) || near(c, p5, IMD5_SPACING_MHZ)) {
            ok = false;
            break outer;
          }
        }
      }
    }
    if (ok) return f;
  }
  return undefined;
}

const csvCell = (v: string) => (/[",\n;]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);

/**
 * Frequency list as CSV for import into Shure Wireless Workbench / Sennheiser WSM (as an
 * inventory or "frequency list"): name, model, band, frequency in MHz with 3 decimals.
 */
export function rfCsv(devices: readonly RfDevice[]): string {
  const rows = [
    ['Name', 'Model', 'Band', 'Frequency (MHz)'],
    ...devices.map((d) => [d.label, d.model, d.band, d.frequency.toFixed(3)]),
  ];
  return rows.map((r) => r.map(csvCell).join(',')).join('\n') + '\n';
}
